<template>
  <div class="configStatusColorsVue">
    <div class="col-md-10 col-sm-9 u-pad-all-25 c-common-box-shadow u-bg-color-fff">
      <h3 class="u-cmn-botmargin">Assign page colours</h3>
      <p class="o-blue-regular-text">
        The colour each availability status uses on the assign page. Fourteen statuses in
        the order ed specified — not alphabetical, and not the order the availability engine
        evaluates them in.
      </p>

      <!-- #1781 — SCAFFOLD. Saying so plainly, because a colour picker that
           visibly changes nothing reads as a bug otherwise. -->
      <p class="csc__scaffold-note" data-testid="csc-scaffold-note">
        <strong>Not live yet.</strong> These colours are saved against this local, but the
        assign page does not read them yet — changing one here will not change the grid.
        The values below are the colours the assign page renders today.
      </p>

      <p v-if="notice" class="csc__notice" data-testid="csc-notice">
        <i class="fa fa-info-circle" aria-hidden="true"></i> {{ notice }}
      </p>
      <p v-if="error" class="csc__error" data-testid="csc-error">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> {{ error }}
      </p>

      <div v-if="loading" class="csc__loading" data-testid="csc-loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true"></i> Loading colours…
      </div>

      <table v-else class="table csc__table" data-testid="csc-table">
        <thead>
          <tr>
            <th class="csc__col-status">Status</th>
            <th class="csc__col-avail">Availability</th>
            <th class="csc__col-swatch">Background</th>
            <th class="csc__col-hex">Hex</th>
            <th class="csc__col-swatch">Font</th>
            <th class="csc__col-hex">Font hex</th>
            <th class="csc__col-preview">Preview</th>
            <th class="csc__col-reset"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in colors" :key="row.status" :data-testid="'csc-row-' + row.status">
            <td>
              {{ row.label }}
              <!-- The stored key, not decoration: it is what the row is stored
                   against and what a support call needs to name. -->
              <span class="csc__key">{{ row.status }}</span>
            </td>
            <td>
              <!-- READ-ONLY. Availability is ed's semantics, not a per-client
                   preference: it says whether a dispatcher can book this person,
                   and it crosses the colour bands (yellow and red each hold both
                   kinds). Letting a Client Admin edit it would let one local
                   redefine bookability. Shown so the config records the meaning;
                   editable only if ed asks. -->
              <span
                class="csc__avail"
                :class="'csc__avail--' + (row.availability || 'unset')"
                :data-testid="'csc-avail-' + row.status"
                :title="availabilityHint(row.availability)"
                >{{ availabilityText(row.availability) }}</span
              >
            </td>
            <td>
              <input
                type="color"
                class="csc__swatch"
                :value="row.hex"
                :data-testid="'csc-swatch-' + row.status"
                @input="setHex(row, $event.target.value)"
              />
            </td>
            <td>
              <input
                type="text"
                class="form-control csc__hex"
                maxlength="7"
                placeholder="#rrggbb"
                spellcheck="false"
                :value="row.hex"
                :class="{ 'csc__hex--invalid': !isValidHex(row.hex) }"
                :data-testid="'csc-hex-' + row.status"
                @input="setHex(row, $event.target.value)"
                @focus="$event.target.select()"
                @blur="commitHex(row)"
              />
              <a
                href="javascript: void(0)"
                class="csc__copy"
                title="Copy hex"
                :data-testid="'csc-copy-' + row.status"
                @click="copy(row.hex)"
                >copy</a
              >
            </td>
            <td>
              <input
                type="color"
                class="csc__swatch"
                :value="row.font_hex"
                :data-testid="'csc-font-swatch-' + row.status"
                @input="setFont(row, $event.target.value)"
              />
            </td>
            <td>
              <input
                type="text"
                class="form-control csc__hex"
                maxlength="7"
                placeholder="#rrggbb"
                :value="row.font_hex"
                :class="{ 'csc__hex--invalid': !isValidHex(row.font_hex) }"
                spellcheck="false"
                :data-testid="'csc-font-hex-' + row.status"
                @input="setFont(row, $event.target.value)"
                @focus="$event.target.select()"
                @blur="commitFont(row)"
              />
              <a
                href="javascript: void(0)"
                class="csc__auto"
                :data-testid="'csc-auto-' + row.status"
                @click="autoFont(row)"
                >auto</a
              >
            </td>
            <td>
              <span
                class="csc__preview"
                :style="{ background: row.hex, color: row.font_hex }"
                :data-testid="'csc-preview-' + row.status"
                >{{ row.label }}</span
              >
            </td>
            <td>
              <a
                href="javascript: void(0)"
                class="csc__reset"
                :data-testid="'csc-reset-' + row.status"
                @click="resetRow(row)"
                >Reset</a
              >
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loading" class="csc__actions">
        <button
          class="c-btn c-btn-primary"
          data-testid="csc-save"
          :disabled="saving || !allValid"
          @click="save()"
        >
          <i v-if="saving" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          {{ saving ? "Saving…" : "Save" }}
        </button>
        <span v-if="!allValid" class="csc__invalid-hint" data-testid="csc-invalid-hint">
          Every background and font colour must be a hex value in the form #rrggbb.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
// #1781 — Config → Assign page colours. SCAFFOLD ONLY.
//
// Lists the twelve availability statuses from AvailabilityCheck::SIGNAL_ORDER
// (the server sends them; this component never hardcodes the list or the order)
// with a per-client colour for each. Seeded with the colours the assign page
// renders today, so a local's starting point is a faithful copy of what they see.
//
// Nothing on the assign page reads these values yet — that is the theming pass.
// The "Not live yet" note above exists so that is obvious to whoever opens this.
import service from "../../services.js";
import { mapGetters } from "vuex";

export default {
  name: "ConfigStatusColors",
  props: ["empDetails"],
  data() {
    return {
      colors: [],
      // The server's is_default hex per status, so Reset does not have to
      // round-trip or duplicate the default map in the frontend.
      defaults: {},
      // Last known-good hex per status, so an invalid edit reverts instead of saving.
      lastValid: {},
      loading: true,
      saving: false,
      notice: "",
      error: "",
    };
  },
  computed: {
    ...mapGetters({
      userToken: "userToken",
      userRole: "userRole",
      userClientID: "userClientID",
    }),
    allValid() {
      return this.colors.every((r) => this.isValidHex(r.hex) && this.isValidHex(r.font_hex));
    },
  },
  created() {
    this.load();
  },
  methods: {
    headers() {
      return {
        Authorization: this.userToken,
        "cs3-context-role": this.userRole,
        "cs3-context-clientId": this.userClientID,
      };
    },
    apiError(e, fallback) {
      const d = e && e.response ? e.response.data : null;
      const msg = d && (d.status_message || d.message);
      return typeof msg === "string" && msg ? msg : fallback;
    },
    availabilityText(v) {
      if (v === "available") return "Available";
      if (v === "not_available") return "Not available";
      if (v === "per_status_rules") return "Per Status Rules";
      return "—";
    },
    availabilityHint(v) {
      // DERIVED, not stored: a confirmed booking on another tenant's job blocks
      // this one only when the times actually collide. Never collapsed to a
      // boolean and never defaulted to "not available" — treating "it depends"
      // as "no" would silently over-block.
      if (v === "per_status_rules")
        return "Derived per status rules — a cross-tenant booking blocks only when the times collide.";
      if (!v) return "Not stated.";
      return "";
    },
    isValidHex(v) {
      return /^#[0-9a-fA-F]{6}$/.test(String(v || ""));
    },
    // Accept what a person actually pastes — "9EDFB6", " #9edfb6 ", "#9EDFB6" —
    // and normalise to #rrggbb lowercase. Anything else is returned unchanged so
    // it fails validation visibly rather than being silently coerced to junk.
    normaliseHex(v) {
      let t = String(v == null ? "" : v).trim();
      if (/^[0-9a-fA-F]{6}$/.test(t)) t = "#" + t;
      return this.isValidHex(t) ? t.toLowerCase() : t;
    },
    // On blur, an invalid value REVERTS to the last good one rather than being
    // stored. ed asked for junk to be rejected, not saved and flagged.
    commitHex(row) {
      if (this.isValidHex(row.hex)) {
        this.lastValid[row.status] = Object.assign({}, this.lastValid[row.status], { hex: row.hex });
        return;
      }
      const prev = (this.lastValid[row.status] || {}).hex;
      if (prev) this.setHex(row, prev);
    },
    commitFont(row) {
      if (this.isValidHex(row.font_hex)) {
        this.lastValid[row.status] = Object.assign({}, this.lastValid[row.status], { font_hex: row.font_hex });
        return;
      }
      const prev = (this.lastValid[row.status] || {}).font_hex;
      if (prev) this.setFont(row, prev);
    },
    copy(value) {
      const done = () => { this.notice = "Copied " + value; };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(() => {});
        return;
      }
      // Older browsers — the assign-page audience is not all on current Chrome.
      const el = document.createElement("textarea");
      el.value = value;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); done(); } catch (e) { /* nothing to do */ }
      document.body.removeChild(el);
    },
    setHex(row, value) {
      // Vue 2 — assign through the array so the row stays reactive.
      const i = this.colors.indexOf(row);
      if (i === -1) return;
      this.$set(this.colors, i, Object.assign({}, row, { hex: this.normaliseHex(value) }));
    },
    setFont(row, value) {
      const i = this.colors.indexOf(row);
      if (i === -1) return;
      this.$set(this.colors, i, Object.assign({}, row, { font_hex: this.normaliseHex(value) }));
    },
    // WCAG 2.x relative luminance — the same calculation the backend uses, so a
    // swatch previewed here matches what a save stores. sRGB is gamma-encoded, so
    // channels are linearised before weighting; a naive average picks the wrong
    // font on mid-tones, which is where legibility is already marginal.
    readableFontFor(bg) {
      if (!this.isValidHex(bg)) return "#000000";
      const lin = [1, 3, 5].map((i) => {
        const c = parseInt(bg.substr(i, 2), 16) / 255;
        return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      const L = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
      const ratio = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
      return ratio(L, 0) >= ratio(L, 1) ? "#000000" : "#ffffff";
    },
    autoFont(row) {
      this.setFont(row, this.readableFontFor(row.hex));
    },
    resetRow(row) {
      const d = this.defaults[row.status];
      if (!d) return;
      const i = this.colors.indexOf(row);
      if (i === -1) return;
      this.$set(this.colors, i, Object.assign({}, row, { hex: d.hex, font_hex: d.font_hex }));
    },
    load() {
      this.loading = true;
      this.error = "";
      service
        .get("client-status-color", this.headers())
        .then((res) => {
          const r = (res && res.data && res.data.result) || {};
          // The server returns all THIRTEEN — `available` first, then the twelve
          // in SIGNAL_ORDER — whether or not a row has been stored, so the form
          // renders complete before a first save.
          this.colors = Array.isArray(r.colors) ? r.colors : [];
          this.defaults = {};
          this.lastValid = {};
          this.colors.forEach((c) => {
            this.defaults[c.status] = { hex: c.hex, font_hex: c.font_hex };
            this.lastValid[c.status] = { hex: c.hex, font_hex: c.font_hex };
          });
        })
        .catch((e) => {
          this.colors = [];
          this.error = this.apiError(e, "Could not load the assign page colours.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      if (!this.allValid) return;
      this.saving = true;
      this.notice = "";
      this.error = "";
      service
        .post(
          "client-status-color",
          { colors: this.colors.map((c) => ({ status: c.status, hex: c.hex, font_hex: c.font_hex })) },
          this.headers()
        )
        .then(() => {
          this.notice = "Colours saved.";
          this.load();
        })
        .catch((e) => {
          this.error = this.apiError(e, "Could not save the assign page colours.");
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.csc__scaffold-note {
  background: #fff8e1;
  border-left: 4px solid #fb8b03;
  padding: 10px 12px;
  margin-bottom: 16px;
}
.csc__notice,
.csc__error {
  padding: 8px 0;
}
.csc__error {
  color: #c0392b;
}
.csc__col-status {
  width: 28%;
}
.csc__col-avail {
  width: 12%;
}
.csc__avail {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.csc__avail--available {
  background: #e6f4ea;
  color: #1e6b34;
}
.csc__avail--not_available {
  background: #fce8e6;
  color: #a5281c;
}
.csc__avail--per_status_rules {
  background: #fef7e0;
  color: #8a6100;
}
.csc__avail--unset {
  color: #999;
}
.csc__col-swatch {
  width: 15%;
}
.csc__col-hex {
  width: 25%;
}
.csc__key {
  display: block;
  font-size: 11px;
  color: #888;
}
.csc__swatch {
  width: 56px;
  height: 32px;
  padding: 0;
  border: 1px solid #ccc;
  background: none;
  cursor: pointer;
}
.csc__hex {
  max-width: 130px;
  text-transform: lowercase;
}
.csc__hex--invalid {
  border-color: #c0392b;
}
.csc__reset,
.csc__copy,
.csc__auto {
  font-size: 12px;
}
.csc__auto,
.csc__copy {
  margin-left: 6px;
}
.csc__col-preview {
  width: 18%;
}
.csc__preview {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
}
.csc__actions {
  margin-top: 16px;
}
.csc__invalid-hint {
  margin-left: 12px;
  color: #c0392b;
  font-size: 12px;
}
</style>
